import { KAssistMark } from '@components/Assistant/ui'
import appStore from '@stores/appStore'
import assistantStore from '@stores/assistantStore'
import chatbotStore from '@stores/chatbotStore'
import { observer } from 'mobx-react'
import React, { useState, useRef, useEffect, useMemo } from 'react'

interface QuickActionItem {
  id: string
  label: string
  icon: React.ReactNode
  action: () => void
  hidden?: boolean
}

const QuickActionDropdown: React.FC = observer(() => {
  const [isOpenDropDown, setIsOpenDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTimeout(() => {
      // Add click event to button
      window.document
        .getElementById('chatbase-bubble-button')
        .addEventListener('click', () => {
          chatbotStore.closeAllChatbots()
        })
    }, 1000)
  }, [])

  // Đóng dropdown khi click bên ngoài
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpenDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Hàm xử lý mở chatbot
  const openChatbot = (chatbotType: string) => {
    chatbotStore.setActiveChatbot(chatbotType)
    setIsOpenDropdown(false)
  }

  // Hàm xử lý mở email feedback
  const openFeedbackEmail = () => {
    const defaultEmail = appStore.currentTenant?.extraProperties?.Email
    const subject = 'System Feedback'
    const body =
      'Dear Support Team,\n\nI would like to provide the following feedback:\n\n'

    const mailtoLink = `mailto:${defaultEmail}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`
    window.open(mailtoLink, '_self')
    setIsOpenDropdown(false)
  }

  // The AI Assistant opens in its own drawer (also Ctrl+K), not as a chatbot.
  const openAssistant = () => {
    chatbotStore.closeAllChatbots()
    assistantStore.openDrawer()
    setIsOpenDropdown(false)
  }

  const quickActions: QuickActionItem[] = [
    {
      id: 'ai-assistant',
      label: 'K-Assist',
      icon: <KAssistMark className="w-5 h-5" />,
      action: () => openAssistant(),
      // Per-tenant "AI Assistant" AI switch (host AI Feature page) and the assistant's own audience.
      hidden: !appStore.isAssistantEnabled,
    },
    // {
    //   id: 'kawaconn',
    //   label: 'Booking Assistant',
    //   icon: (
    //     <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    //       <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 2.98.97 4.29L1 23l6.71-1.97C9.02 21.64 10.46 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-2h2v2zm2.07-7.75l-.9.92C11.45 10.9 11 11.5 11 13h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H6c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
    //     </svg>
    //   ),
    //   action: () => openChatbot('kawaconn'),
    //   // Per-tenant "Booking Assistant" AI switch (host AI Feature page).
    //   hidden:
    //     !appStore.isAIFeatureEnabled('AIBookingAssistant') ||
    //     !appStore.isAdminOrStaffOrCM,
    // },
    // {
    //   id: 'knowledge-base',
    //   label: 'Usage Help',
    //   icon: (
    //     <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    //       <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
    //     </svg>
    //   ),
    //   action: () => openChatbot('knowledge-base'),
    //   hidden: false,
    // },
    {
      id: 'support',
      label: 'Send Feedback',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      ),
      action: () => openFeedbackEmail(),
      hidden: false,
    },
  ].filter(i => !i.hidden)

  // const isOpenChatbot = useMemo(() => {
  //   return !!chatbotStore.activeChatbot && !isOpenDropDown
  // }, [chatbotStore.activeChatbot, isOpenDropDown])

  return (
    <>
      {/* QuickAction Dropdown Container */}
      <div ref={dropdownRef} className="fixed bottom-14 right-6 z-[101]">
        {/* Dropdown Menu */}
        {isOpenDropDown && (
          <div className="absolute bottom-16 right-0 mb-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 animate-in slide-in-from-bottom-2 duration-200">
            {quickActions.map(action => (
              <button
                key={action.id}
                onClick={action.action}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3"
              >
                <div className="flex-shrink-0 text-blue-600">{action.icon}</div>
                <span className="text-gray-700 font-medium text-sm">
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Main Action Button - Khi dropdown đóng */}
        {/* {!isOpenChatbot && ( */}
        <button
          onClick={() => setIsOpenDropdown(!isOpenDropDown)}
          className="w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group relative"
        >
          {/* Chat Icon SVG */}
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>

          {/* Ripple effect */}
          <div className="absolute inset-0 rounded-full bg-blue-400 opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
        </button>
        {/* )} */}

        {/* Close Button - Khi dropdown mở */}
        {/* {isOpenChatbot && (
          <button
            onClick={() => {
              setIsOpenDropdown(false)
              if (chatbotStore.activeChatbot) {
                chatbotStore.closeAllChatbots()
              }
            }}
            className="w-14 h-14 bg-blue-600 hover:bg-grey-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group relative"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>

            <div className="absolute inset-0 rounded-full bg-gray-400 opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
          </button>
        )} */}
      </div>
    </>
  )
})

export default QuickActionDropdown
